import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  listForgeStrategies,
  patchForgeStrategy,
  deleteForgeStrategy,
  logForgeStrategy,
} from "@/lib/forgeApi";
import { BookOpen, Check, X, Trash2, Tag, PlusCircle, ChevronRight, Bell } from "lucide-react";
import { toast } from "sonner";
import QuickAlertModal from "@/components/alerts/QuickAlertModal";

const PLAYBOOK_OPTIONS = [
  "Wheel Strategy",
  "Core Equity Income",
  "Tail-Risk Hedge",
  "Directional Swing",
  "Earnings Vol Play",
  "Capital-Efficient Replacement",
  "Mean Reversion",
  "Other",
];

const STATUS_COLORS = {
  open: "text-[#00E5FF] border-[#00E5FF]/30 bg-[#00E5FF]/5",
  closed: "text-white/60 border-white/15 bg-white/5",
  rolled: "text-[#FFD60A] border-[#FFD60A]/30 bg-[#FFD60A]/5",
};

const fmt$ = (n) =>
  n == null ? "—" : (n >= 0 ? "+" : "−") + "$" + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtPct = (n) =>
  n == null ? "—" : (n >= 0 ? "+" : "") + Number(n).toFixed(2) + "%";

export const StrategyLog = ({ currentStrategy, ticker, inputs, onLogged }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [tag, setTag] = useState(PLAYBOOK_OPTIONS[0]);
  const [showLog, setShowLog] = useState(false);
  const [alertModal, setAlertModal] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const data = await listForgeStrategies();
      setRows(data.items || []);
    } catch (e) {
      console.error("strategies fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onSave = async () => {
    if (!user) {
      toast.error("Sign in to journal strategies");
      return;
    }
    if (!currentStrategy?.metrics) {
      toast.error("Build a strategy first");
      return;
    }
    try {
      await logForgeStrategy({
        symbol: ticker || "—",
        template_id: currentStrategy.template.id,
        template_name: currentStrategy.template.name,
        playbook_tag: tag,
        spot_at_open: inputs.spot,
        dte_at_open: inputs.dte,
        iv_at_open: inputs.iv,
        legs: currentStrategy.metrics.legs.map(({ greeks, mid, ...rest }) => rest),
        metrics: {
          net_premium: currentStrategy.metrics.net_premium,
          max_profit: currentStrategy.metrics.max_profit,
          max_loss: currentStrategy.metrics.max_loss,
          breakevens: currentStrategy.metrics.breakevens,
          pop: currentStrategy.metrics.pop,
          greeks: currentStrategy.metrics.greeks,
        },
      });
      toast.success(`Logged ${currentStrategy.template.name} on ${ticker}`);
      setShowLog(false);
      refresh();
      onLogged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log strategy");
    }
  };

  const onClose = async (id) => {
    const txt = window.prompt("Closing PnL in dollars (e.g. 125 or -78):");
    if (txt == null) return;
    const pnl = parseFloat(txt);
    if (Number.isNaN(pnl)) {
      toast.error("Invalid number");
      return;
    }
    const row = rows.find((r) => r.id === id);
    const ref = row?.metrics?.net_premium != null ? Math.abs(row.metrics.net_premium) : null;
    const pnl_pct = ref && ref > 0 ? (pnl / ref) * 100 : null;
    try {
      await patchForgeStrategy(id, { status: "closed", close_pnl: pnl, close_pnl_pct: pnl_pct });
      toast.success("Closed");
      refresh();
    } catch (e) {
      toast.error("Failed to close");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this logged strategy?")) return;
    try {
      await deleteForgeStrategy(id);
      refresh();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const filtered =
    filter === "all" ? rows : rows.filter((r) => (r.status || "open") === filter);

  return (
    <div
      className="bg-[#0c0c0e] border border-white/10 rounded-xl p-4 sm:p-5"
      data-testid="strategy-log"
    >
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#A98CFF]" />
          <h3 className="text-sm font-medium tracking-tight">Strategy Journal</h3>
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/40">
            {rows.length}/100
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {["all", "open", "closed", "rolled"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`forge-journal-filter-${s}`}
              className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded border ${
                filter === s
                  ? "border-white/40 text-white bg-white/10"
                  : "border-white/10 text-white/55 hover:text-white"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Log current strategy */}
      <div className="rounded-lg border border-white/10 bg-[#0a0a0c] p-3 mb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-[11px] text-white/55">
            <PlusCircle className="w-3.5 h-3.5 text-[#00C805]" />
            {currentStrategy?.metrics ? (
              <span>
                Log{" "}
                <span className="text-white font-medium">{currentStrategy.template.name}</span>{" "}
                on <span className="num text-white">{ticker}</span>
              </span>
            ) : (
              <span>Build a strategy to log it here</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowLog((v) => !v)}
            disabled={!currentStrategy?.metrics || !user}
            data-testid="forge-journal-add-btn"
            className="text-[11px] bg-white text-black px-2.5 py-1 rounded font-medium hover:bg-white/90 disabled:opacity-40"
          >
            {showLog ? "Cancel" : user ? "Add tag & save" : "Sign in"}
          </button>
        </div>
        {showLog && currentStrategy?.metrics && user && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Tag className="w-3.5 h-3.5 text-white/40" />
            <select
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              data-testid="forge-journal-tag-select"
              className="bg-[#050505] border border-white/10 rounded px-2 py-1 text-[11px] text-white"
            >
              {PLAYBOOK_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={onSave}
              data-testid="forge-journal-save-btn"
              className="text-[11px] bg-[#00C805] text-black px-3 py-1 rounded font-medium hover:bg-[#00C805]/90"
            >
              Save to journal
            </button>
          </div>
        )}
      </div>

      {/* List */}
      {!user ? (
        <p className="text-[12px] text-white/50 italic px-1">
          Sign in to track strategies and unlock Alpha Optimization insights.
        </p>
      ) : loading ? (
        <p className="text-[11px] text-white/40">Loading…</p>
      ) : !filtered.length ? (
        <p className="text-[11px] text-white/40 italic">
          No {filter !== "all" ? filter + " " : ""}strategies yet.
        </p>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const status = r.status || "open";
            return (
              <div
                key={r.id}
                className="rounded-lg border border-white/10 bg-[#0a0a0c] p-3 flex items-center justify-between gap-3 flex-wrap"
                data-testid={`forge-journal-row-${r.id}`}
              >
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                  <span
                    className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border ${STATUS_COLORS[status]}`}
                  >
                    {status}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium text-white truncate">
                      <span className="num">{r.symbol}</span>
                      <ChevronRight className="inline w-3 h-3 text-white/30 mx-0.5" />
                      {r.template_name}
                    </div>
                    <div className="text-[10px] text-white/50 mt-0.5">
                      {r.playbook_tag} · DTE {r.dte_at_open} · IV {(r.iv_at_open * 100).toFixed(0)}% ·{" "}
                      Spot ${r.spot_at_open}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {status === "closed" && (
                    <div className="text-right">
                      <div
                        className={`num text-sm font-medium ${
                          (r.close_pnl ?? 0) >= 0 ? "text-[#00C805]" : "text-[#FF3B30]"
                        }`}
                      >
                        {fmt$(r.close_pnl)}
                      </div>
                      <div className="text-[9px] text-white/40">{fmtPct(r.close_pnl_pct)}</div>
                    </div>
                  )}
                  {status === "open" && (
                    <button
                      type="button"
                      onClick={() => onClose(r.id)}
                      data-testid={`forge-journal-close-${r.id}`}
                      className="text-[10px] uppercase tracking-[0.18em] border border-[#00C805]/40 text-[#00C805] px-2 py-1 rounded hover:bg-[#00C805]/10 inline-flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" /> Close
                    </button>
                  )}
                  {status === "open" && !!(r.metrics?.breakevens || []).length && (
                    <button
                      type="button"
                      data-testid={`forge-journal-alert-${r.id}`}
                      title="Alert at breakeven"
                      onClick={() =>
                        setAlertModal({
                          title: `Alert when ${r.symbol} crosses breakeven`,
                          defaults: {
                            kind: "breakeven",
                            symbol: r.symbol,
                            breakeven: r.metrics.breakevens[0],
                            direction: r.metrics.greeks?.delta >= 0 ? "below" : "above",
                            strategy_id: r.id,
                            note: `${r.template_name} on ${r.symbol}`,
                          },
                        })
                      }
                      className="text-white/40 hover:text-[#FFD60A] p-1 rounded"
                    >
                      <Bell className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDelete(r.id)}
                    data-testid={`forge-journal-delete-${r.id}`}
                    className="text-white/40 hover:text-[#FF3B30] p-1 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <QuickAlertModal
        open={!!alertModal}
        onClose={() => setAlertModal(null)}
        defaults={alertModal?.defaults || {}}
        title={alertModal?.title}
        lockKind
      />
    </div>
  );
};

export default StrategyLog;
